/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, batch } from '@reatom/framework'
import { BigNumber } from 'bignumber.js'
import * as DateF from 'date-fns'

import { CurrencyAddress } from '@root/entities/currency'
import { Network } from '@root/entities/network'
import { pairDtoUpdateReceivedEvent, type PairModel } from '@root/entities/pair'
import { TradeSettings } from '@root/entities/trade-settings'
import { WalletId, walletsByIdAtom } from '@root/entities/wallet'
import { reatomBigNumberComputed } from '@root/shared/lib'

import { LimitOrderSwap } from '../model'
import { LimitOrderId } from '../model/limit-order-id'

import type * as BlockchainTypes from '@blade/blockchain-types'
import type { TradeApi } from '@root/shared/api'

export const limitOrderDtoReceivedEvent = action<TradeApi.LimitOrderDto>(
  'limitOrderDtoReceivedEvent',
)
limitOrderDtoReceivedEvent.onCall((ctx, dto) => {
  batch(ctx, () => {
    pairDtoUpdateReceivedEvent(ctx, dto.pairMeta)
  })
})

export const reatomLimitOrderBase = ({
  dto,
  pair,
}: {
  dto: TradeApi.LimitOrderDto
  pair: PairModel
}) => {
  //#region Static
  const id = LimitOrderId(dto.id)
  const type = dto.type
  const createdAt = new Date(dto.createdAt)
  const walletId = WalletId(dto.walletId)
  const network = Network.fromDto(dto.network)
  const currencyAddress = CurrencyAddress(dto.tokenAddress)
  const deadlineMs = dto.deadlineMs
  const deadlineAt = new Date(dto.deadlineAt)
  const triggerType = dto.trigger.triggerType

  const costAtCreationUsd = BigNumber(dto.costAtCreationUsd)
  const costAtCreationNative = BigNumber(dto.costAtCreationNative)
  const marketCapAtCreationUsd = BigNumber(dto.marketCapAtCreationUsd)
  //#endregion

  //#region State
  const updatedAtAtom = atom(new Date(dto.updatedAt))
  const closedAtAtom = atom(dto.closedAt ? new Date(dto.closedAt) : undefined)
  const statusAtom = atom(dto.status)
  const swapSettingsAtom = atom(
    TradeSettings.fromDto(dto.swapSettings, network),
  )

  const swapsAtom = atom(dto.swaps.map(LimitOrderSwap.fromDto))

  const triggerValueHighAtom = atom(
    dto.trigger.valueHigh ? BigNumber(dto.trigger.valueHigh) : undefined,
  )

  const triggerValueLowAtom = atom(
    dto.trigger.valueLow ? BigNumber(dto.trigger.valueLow) : undefined,
  )

  //#endregion

  //#region Computed

  const walletAtom = atom((ctx) => {
    const wallets = ctx.spy(walletsByIdAtom)
    return wallets.get(walletId)
  })
  const finishedSwapsAtom = atom((ctx) => {
    const swaps = ctx.spy(swapsAtom)
    return swaps.filter(
      (swap) => swap.status === 'finished' && swap.swapDetails,
    )
  })

  const fulfilledAmountAtom = reatomBigNumberComputed((ctx) => {
    const swaps = ctx.spy(finishedSwapsAtom)
    return swaps.reduce((acc, swap) => {
      const amount =
        type === 'buy'
          ? swap.swapDetails?.amountIn
          : swap.swapDetails?.amountOut

      if (!amount) {
        return acc
      }
      return acc.plus(amount)
    }, BigNumber(0))
  })

  const allTimeTokenAmountAtom = reatomBigNumberComputed((ctx) => {
    const swaps = ctx.spy(finishedSwapsAtom)
    return swaps.reduce(
      (acc, swap) => acc.plus(swap.rollingStats.allTimeTokenAmount),
      BigNumber(0),
    )
  })

  const allTimeExecutionBuyPriceAtom = atom((ctx) => {
    const swaps = ctx.spy(finishedSwapsAtom)

    return swaps.reduce((acc, swap) => {
      return acc.plus(
        swap.rollingStats.averageBuyPrice.multipliedBy(
          swap.rollingStats.allTimeTokenAmount,
        ),
      )
    }, BigNumber(0))
  })
  const allTimeExecutionSellPriceAtom = atom((ctx) => {
    const swaps = ctx.spy(finishedSwapsAtom)

    return swaps.reduce((acc, swap) => {
      return acc.plus(
        swap.rollingStats.averageSellPrice.multipliedBy(
          swap.rollingStats.allTimeTokenAmount,
        ),
      )
    }, BigNumber(0))
  })

  const averageExecutionBuyPriceAtom = atom((ctx) => {
    const status = ctx.spy(statusAtom)
    if (status !== 'completed') {
      return BigNumber(0)
    }
    const allTimeExecutionPrice = ctx.spy(allTimeExecutionBuyPriceAtom)
    const allTimeTokenAmount = ctx.spy(allTimeTokenAmountAtom)
    return allTimeExecutionPrice.dividedBy(allTimeTokenAmount)
  })
  const averageExecutionSellPriceAtom = atom((ctx) => {
    const status = ctx.spy(statusAtom)
    if (status !== 'completed') {
      return BigNumber(0)
    }
    const allTimeExecutionPrice = ctx.spy(allTimeExecutionSellPriceAtom)
    const allTimeTokenAmount = ctx.spy(allTimeTokenAmountAtom)
    return allTimeExecutionPrice.dividedBy(allTimeTokenAmount)
  })

  const allTimeExecutionBuyMarketCapAtom = atom((ctx) => {
    const swaps = ctx.spy(finishedSwapsAtom)

    return swaps.reduce((acc, swap) => {
      return acc.plus(
        swap.rollingStats.averageBuyMarketCap.multipliedBy(
          swap.rollingStats.allTimeTokenAmount,
        ),
      )
    }, BigNumber(0))
  })

  const allTimeExecutionSellMarketCapAtom = atom((ctx) => {
    const swaps = ctx.spy(finishedSwapsAtom)

    return swaps.reduce((acc, swap) => {
      return acc.plus(
        swap.rollingStats.averageSellMarketCap.multipliedBy(
          swap.rollingStats.allTimeTokenAmount,
        ),
      )
    }, BigNumber(0))
  })

  const averageExecutionBuyMarketCapAtom = atom((ctx) => {
    const allTimeExecutionMarketCap = ctx.spy(allTimeExecutionBuyMarketCapAtom)
    const allTimeTokenAmount = ctx.spy(allTimeTokenAmountAtom)
    return allTimeExecutionMarketCap.dividedBy(allTimeTokenAmount)
  })
  const averageExecutionSellMarketCapAtom = atom((ctx) => {
    const allTimeExecutionMarketCap = ctx.spy(allTimeExecutionSellMarketCapAtom)
    const allTimeTokenAmount = ctx.spy(allTimeTokenAmountAtom)
    return allTimeExecutionMarketCap.dividedBy(allTimeTokenAmount)
  })

  //#endregion

  const updateFromDtoAction = action(
    (ctx, update: BlockchainTypes.LimitOrder | TradeApi.LimitOrderDto) => {
      const currentUpdatedAt = ctx.get(updatedAtAtom)
      const updatedAt = new Date(update.updatedAt)
      if (DateF.isAfter(currentUpdatedAt, updatedAt)) {
        return
      }
      updatedAtAtom(ctx, updatedAt)
      closedAtAtom(ctx, update.closedAt ? new Date(update.closedAt) : undefined)
      statusAtom(ctx, update.status)

      if ('swaps' in update) {
        swapsAtom(ctx, update.swaps.map(LimitOrderSwap.fromDto))
      }

      triggerValueHighAtom(
        ctx,
        update.trigger.valueHigh
          ? BigNumber(update.trigger.valueHigh)
          : undefined,
      )
      triggerValueLowAtom(
        ctx,
        update.trigger.valueLow
          ? BigNumber(update.trigger.valueLow)
          : undefined,
      )
    },
  )

  return {
    id,
    createdAt,
    walletId,
    network,
    currencyAddress,
    costAtCreationUsd,
    costAtCreationNative,
    marketCapAtCreationUsd,
    triggerType,
    deadlineMs,
    deadlineAt,

    updatedAt: updatedAtAtom,
    closedAt: closedAtAtom,
    status: statusAtom,
    swapSettings: swapSettingsAtom,
    triggerValueHigh: triggerValueHighAtom,
    triggerValueLow: triggerValueLowAtom,
    swaps: swapsAtom,
    fulfilledAmount: fulfilledAmountAtom,
    allTimeTokenAmount: allTimeTokenAmountAtom,
    allTimeExecutionBuyPrice: allTimeExecutionBuyPriceAtom,
    averageExecutionBuyPrice: averageExecutionBuyPriceAtom,
    allTimeExecutionBuyMarketCap: allTimeExecutionBuyMarketCapAtom,
    averageExecutionBuyMarketCap: averageExecutionBuyMarketCapAtom,
    allTimeExecutionSellPrice: allTimeExecutionSellPriceAtom,
    averageExecutionSellPrice: averageExecutionSellPriceAtom,
    allTimeExecutionSellMarketCap: allTimeExecutionSellMarketCapAtom,
    averageExecutionSellMarketCap: averageExecutionSellMarketCapAtom,
    wallet: walletAtom,

    pair,

    updateFromDto: updateFromDtoAction,
  }
}
